// Libraries
import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { InterventionStore } from 'src/app/services/stores/intervention.store';

// Interfaces
import {
  defaultIntervention,
  Intervention,
  InterventionEvaluationResult,
  PostIntervention,
  PutIntervention,
} from 'src/app/interfaces/api/intervention';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { defaultUser, User } from 'src/app/interfaces/api/user';

// Classes
import { Pop } from 'src/app/classes/shared/pop';

// Components
import { SearchSelectComponent } from 'projects/ui-library/src/lib/components/search-select/search-select.component';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { ButtonComponent } from 'ui-library';

// Displays and parses dates as JJ/MM/AAAA, matching the format required by the intervention validation rules.
const INTERVENTION_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null,
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

@Component({
  selector: 'app-intervention-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    TranslateModule,
    ButtonComponent,
    SearchSelectComponent,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: INTERVENTION_DATE_FORMATS },
  ],
  templateUrl: './intervention-drawer.component.html',
  styleUrls: ['./intervention-drawer.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('open', style({ transform: 'translateX(0)' })),
      state('closed', style({ transform: 'translateX(100%)' })),
      transition('open <=> closed', [animate('250ms ease-in-out')]),
    ]),
  ],
})
export class InterventionDrawerComponent implements OnChanges, OnDestroy {
  private readonly interventionStore: InterventionStore = inject(InterventionStore);
  private readonly userService: UserService = inject(UserService);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly pop: Pop = inject(Pop);

  @Input() open: boolean = false;
  @Input() intervention: Intervention | null = null;
  @Input() mode: 'add' | 'edit' = 'add';
  @Input() projectId: string = '';
  @Input() adminMode: boolean = false;
  @Input() presetTracker: string | null = null;
  @Input() disableAnimation: boolean = false;

  @ViewChild('firstField', { read: ElementRef }) firstField?: ElementRef<HTMLElement>;
  @ViewChild('interventionForm') ngForm?: NgForm;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Intervention>();
  @Output() deleted = new EventEmitter<string>();

  form: Partial<Intervention> = { ...defaultIntervention, evaluation: {} };
  users: User[] = [];
  saving: boolean = false;
  dirty: boolean = false;
  saveError: string | null = null;
  destroy$ = new Subject<void>();
  private dueDateRaw: string = '';

  readonly referential = this.interventionStore.referential;
  readonly referentialError = this.interventionStore.referentialError;
  readonly evaluationOptions: InterventionEvaluationResult[] = ['EFFICACE', 'PARTIELLEMENT_EFFICACE', 'INEFFICACE'];

  get categories(): string[] {
    return Object.keys(this.referential());
  }

  get subTypeOptions(): string[] {
    const category = this.form.category;
    return (category && this.referential()[category]) || [];
  }

  usersForSelect: Array<{ id: string; fullName: string }> = [];
  trackersForSelect: Array<{ uuid: string; label: string }> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.resetForm();
      this.loadUsers();
      this.loadTrackers();
      return;
    }

    if (changes['intervention'] && this.open && this.mode === 'edit' && !this.dirty) {
      this.resetForm();
    }
  }

  onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'open') this.firstField?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetForm(): void {
    this.saving = false;
    this.saveError = null;
    this.dueDateRaw = '';

    if (this.mode === 'edit' && this.intervention) {
      this.form = {
        ...defaultIntervention,
        ...this.intervention,
        trackers: [...(this.intervention.trackers ?? [])],
        evaluation: { ...this.intervention.evaluation },
      };
    } else {
      this.form = {
        ...defaultIntervention,
        project: this.projectId,
        trackers: this.presetTracker ? [this.presetTracker] : [],
        assignee: this.authService.currentUser().id,
        dueDate: new Date().toISOString(),
        evaluation: {},
      };
    }

    this.ngForm?.resetForm({
      category: this.form.category,
      subType: this.form.subType,
      trackers: this.form.trackers,
      description: this.form.description,
      dueDate: this.form.dueDate,
      evaluationResult: this.form.evaluation?.result ?? null,
      evaluationDate: this.form.evaluation?.date,
      evaluationComment: this.form.evaluation?.comment,
    });

    this.dirty = false;
  }

  private loadUsers(): void {
    if (!this.projectId) return;

    this.userService.getUsersLight(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (users: User[]) => {
        this.users = users || [];
        this.ensureAssignedUsersVisible();
        this.refreshUsersForSelect();
      },
      error: () => {
        this.users = [];
        this.refreshUsersForSelect();
      },
    });
  }

  private ensureAssignedUsersVisible(): void {
    const currentUser = this.authService.currentUser();

    [this.form.applicant, this.form.assignee].forEach(id => {
      if (id && !this.users.some(u => u.id === id)) {
        this.users.push(
          id === currentUser.id
            ? currentUser
            : { ...defaultUser, id, firstName: this.translate.instant('interventions.inactive_account'), lastName: '' },
        );
      }
    });
  }

  private refreshUsersForSelect(): void {
    this.usersForSelect = this.users.map(u => ({
      id: u.id,
      fullName: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || u.id,
    }));
  }

  private loadTrackers(): void {
    if (this.adminMode && !this.intervention?.sourceDatabase) {
      this.trackersForSelect = [];
      return;
    }

    const trackers$ = this.adminMode
      ? this.trackerService.getTrackersForDatabase(this.intervention!.sourceDatabase!)
      : this.trackerService.getTrackers();

    trackers$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (trackers: Tracker[]) => {
        this.trackersForSelect = (trackers || []).map(t => ({ uuid: t.uuid, label: t.name }));
      },
      error: () => {
        this.trackersForSelect = [];
      },
    });
  }

  markDirty(): void {
    this.dirty = true;
    this.saveError = null;
  }

  onCategoryChanged(): void {
    this.form.subType = '';
    this.markDirty();
  }

  onApplicantChanged(value: string | null): void {
    this.form.applicant = value ?? undefined;
    this.markDirty();
  }

  onAssigneeChanged(value: string | null): void {
    this.form.assignee = value ?? undefined;
    this.markDirty();
  }

  onEvaluationResultChanged(value: InterventionEvaluationResult | null): void {
    if (value) {
      if (!this.form.evaluation!.date) this.form.evaluation!.date = new Date().toISOString();
    } else {
      this.form.evaluation!.date = undefined;
    }

    this.markDirty();
  }

  onDueDateInput(event: Event): void {
    this.dueDateRaw = ((event.target as HTMLInputElement)?.value ?? '').trim();
    this.markDirty();
  }

  private parseStrictDate(raw: string): Date | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);

    const isRealCalendarDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    return isRealCalendarDate ? date : null;
  }

  private validate(): string | null {
    if (!this.form.category) return this.translate.instant('interventions.error.category_required');
    if (!this.form.subType || !this.subTypeOptions.includes(this.form.subType)) {
      return this.translate.instant('interventions.error.subType_required');
    }

    if (this.form.description && this.form.description.length > 2000) {
      return this.translate.instant('interventions.error.description_max_length');
    }

    if (!this.form.assignee) return this.translate.instant('interventions.error.assignee_required');

    if (this.dueDateRaw) {
      const parsedDueDate = this.parseStrictDate(this.dueDateRaw);
      if (!parsedDueDate) return this.translate.instant('interventions.error.due_date_invalid');
      this.form.dueDate = parsedDueDate.toISOString();
    }

    const result = this.form.evaluation?.result;
    if (result && !this.evaluationOptions.includes(result)) {
      return this.translate.instant('interventions.error.evaluation_result_invalid');
    }

    if (result) {
      const evaluationDate = this.form.evaluation?.date;
      const referenceDate = this.intervention?.createdDate ?? new Date().toISOString();
      const isBeforeCreation =
        evaluationDate && new Date(evaluationDate).getTime() < new Date(referenceDate).getTime();

      if (!evaluationDate || isNaN(new Date(evaluationDate).getTime()) || isBeforeCreation) {
        return this.translate.instant('interventions.error.evaluation_date_required');
      }
    }

    if (this.form.evaluation?.comment && this.form.evaluation.comment.length > 1000) {
      return this.translate.instant('interventions.error.evaluation_comment_max_length');
    }

    return null;
  }

  save(): void {
    const error = this.validate();
    if (error) {
      this.saveError = error;
      this.pop.error(error);
      return;
    }

    this.saveError = null;
    this.saving = true;
    const evaluation = this.form.evaluation?.result ? this.form.evaluation : undefined;

    if (this.mode === 'edit' && this.form.id) {
      const put: PutIntervention = {
        id: this.form.id,
        category: this.form.category,
        subType: this.form.subType,
        description: this.form.description,
        applicant: this.form.applicant,
        assignee: this.form.assignee,
        dueDate: this.form.dueDate,
        trackers: this.form.trackers,
        evaluation,
      };

      this.interventionStore.updateIntervention(put).pipe(takeUntil(this.destroy$)).subscribe({
        next: (updated) => {
          this.saving = false;
          this.dirty = false;
          this.pop.toast(this.translate.instant('interventions.save_success'), 'success', 4000, 'save');
          this.saved.emit(updated);
        },
        error: (err) => {
          this.saving = false;
          console.error(err);
          const message = this.translate.instant('interventions.save_error');
          this.saveError = message;
          this.pop.error(message);
        },
      });
    } else {
      const post: PostIntervention = {
        project: this.projectId,
        category: this.form.category!,
        subType: this.form.subType!,
        description: this.form.description,
        applicant: this.form.applicant,
        assignee: this.form.assignee!,
        dueDate: this.form.dueDate,
        trackers: this.form.trackers,
        evaluation,
      };

      this.interventionStore.addIntervention(post).pipe(takeUntil(this.destroy$)).subscribe({
        next: (created) => {
          this.saving = false;
          this.dirty = false;
          this.pop.toast(this.translate.instant('interventions.create_success'), 'success', 4000, 'save');
          this.saved.emit(created);
        },
        error: (err) => {
          this.saving = false;
          console.error(err);
          const message = this.translate.instant('interventions.save_error');
          this.saveError = message;
          this.pop.error(message);
        },
      });
    }
  }

  cancel(): void {
    if (this.dirty) {
      this.pop.confirm(this.translate.instant('interventions.confirm_close'), (confirm: any) => {
        if (confirm.ok) this.closed.emit();
      });
    } else {
      this.closed.emit();
    }
  }

  remove(): void {
    if (!this.form.id) return;

    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        title: this.translate.instant('interventions.title_one'),
        name: `${this.form.category} — ${this.form.subType}`,
      },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (!res) return;

      this.interventionStore.removeIntervention(this.form.id!).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.pop.toast(this.translate.instant('interventions.delete_success'), 'success', 4000, 'delete');
          this.deleted.emit(this.form.id!);
        },
        error: (err) => {
          console.error(err);
          this.pop.error(this.translate.instant('interventions.delete_error'));
        },
      });
    });
  }
}
